// Moldura do site: cabeçalho fixo com navegação e conta, conteúdo, rodapé.
// A língua vem do endereço (sem prefixo → língua por omissão, hoje EN; /<língua>… → outra língua ativa)
// e acompanha a navegação sem recarregar. O botão de língua só aparece com 2+ línguas ativas (ACTIVE_LANGS).
import type { SiteSection } from "@ecliptica/config/site-sections";
import { Logo } from "@ecliptica/game-ui";
import { LanguageSwitch, useTranslation } from "@ecliptica/i18n";
import { useEffect, useLayoutEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { helpPath, langFromPath, pathFor, sitePath, translatePath } from "../lib/lang";
import { pageMeta } from "../lib/meta";
import { downloadPanels, useSiteSections } from "../lib/sections";
import { Footer } from "./Footer";
import { UserMenu } from "./UserMenu";

/** Ligações do menu: âncora na página inicial (= id da secção e chave nav.*) → parte do site que a liga. */
const NAV: [anchor: string, section: SiteSection][] = [
  ["game", "game"],
  ["cards", "cards"],
  ["world", "world"],
  ["books", "books"],
  ["comic", "comic"],
  ["download", "downloads"],
];

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  const home = pathFor(lang);
  const on = useSiteSections();

  useLayoutEffect(() => {
    if (i18n.language !== lang) void i18n.changeLanguage(lang);
  }, [i18n, lang]);

  // Título e descrição da página acompanham a língua (o HTML pré-renderizado já vem com os certos).
  useEffect(() => {
    const meta = pageMeta(pathname, lang, (k, o) => t(k, o) as string);
    document.title = meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", meta.description);
  }, [t, pathname, lang]);

  // Só em desenvolvimento, para capturas: ?ver=<secção> mostra só essa secção (ex.: ?ver=cards).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const only = new URLSearchParams(window.location.search).get("ver");
    if (!only) return;
    document.querySelectorAll<HTMLElement>("main > section[id]").forEach((s) => {
      if (s.id !== only) s.style.display = "none";
    });
  }, []);

  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-gold focus:px-4 focus:py-2 focus:font-semibold focus:text-night focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {t("a11y.skip")}
      </a>
      <header className="fixed inset-x-0 top-0 z-30 border-b bg-gradient-to-b from-[rgba(11,12,34,.92)] to-[rgba(11,12,34,.6)] backdrop-blur-md">
        <div className="flex h-16 items-center gap-6 px-4 sm:px-6">
          <a href={`${home}#top`} aria-label="Ecliptica" className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Logo className="text-[11px] sm:text-sm" />
          </a>
          <nav className="hidden flex-1 flex-wrap gap-5 text-sm md:flex">
            {NAV.filter(([, s]) => (s === "downloads" ? downloadPanels(on).length > 0 : on[s])).map(([k]) =>
              k === "world" || k === "cards" ? (
                // O Mundo e as Cartas têm página própria (a amostra na página inicial leva até lá).
                <Link key={k} to={sitePath(lang, k)} className="opacity-85 hover:text-gold-bright hover:opacity-100">
                  {t(`nav.${k}`)}
                </Link>
              ) : (
                <a key={k} href={`${home}#${k}`} className="opacity-85 hover:text-gold-bright hover:opacity-100">
                  {t(`nav.${k}`)}
                </a>
              ),
            )}
            <Link to={helpPath(lang, "help")} className="opacity-85 hover:text-gold-bright hover:opacity-100">
              {t("nav.help")}
            </Link>
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <LanguageSwitch onSwitch={(l) => navigate(translatePath(pathname, l) + window.location.search)} />
            <UserMenu lang={lang} />
          </div>
        </div>
      </header>
      <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
        <Outlet />
      </main>
      <Footer lang={lang} sections={on} />
    </div>
  );
}
